import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Car,
  ChevronDown,
  Fuel,
  HelpCircle,
  LayoutGrid,
  LogOut,
  Menu,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { ShiftLogo } from "@/components/layout/ShiftLogo";
import { cn } from "@/lib/cn";
import { useAuth } from "@/contexts/AuthContext";

const mainNav = [
  { href: "/", label: "Home", icon: LayoutGrid },
  { href: "/cars", label: "Cars", icon: Car },
  { href: "/gas-stations", label: "Fuel", icon: Fuel },
  { href: "/businesses", label: "Businesses", icon: Building2 },
  { href: "/users", label: "Users", icon: Users },
];

export function Sidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { signOut, adminProfile, session } = useAuth();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const displayName =
    adminProfile?.firstName && adminProfile?.lastName
      ? `${adminProfile.firstName} ${adminProfile.lastName}`
      : session?.user.email ?? "Admin";

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
    navigate("/login", { replace: true });
    setSigningOut(false);
    setOpen(false);
  }

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    if (href === "/gas-stations") {
      return pathname.startsWith("/gas-stations") || pathname.startsWith("/fuel-activity");
    }
    return pathname.startsWith(href);
  };

  const NavLinks = ({ items, label }: { items: typeof mainNav; label: string }) => (
    <nav className="space-y-0.5">
      <p className="mb-2 px-3 text-xs font-bold uppercase tracking-wider text-muted-light">{label}</p>
      {items.map(({ href, label: itemLabel, icon: Icon }) => {
        const active = isActive(href);
        return (
          <Link
            key={href}
            to={href}
            onClick={() => setOpen(false)}
            className={cn("nav-item", active && "nav-item-active")}
          >
            {active ? (
              <motion.span
                layoutId="sidebar-active-pill"
                className="absolute inset-0 rounded-full bg-accent-subtle"
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
              />
            ) : null}
            <Icon className="relative z-10 h-4 w-4 shrink-0" strokeWidth={active ? 2 : 1.75} />
            <span className="relative z-10">{itemLabel}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed left-4 top-4 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-surface shadow-[var(--shadow-elevation)] lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
        />
      ) : null}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[var(--sidebar-width)] flex-col bg-surface px-3 py-5 shadow-[var(--shadow-elevation)] transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] lg:translate-x-0",
          "lg:inset-y-4 lg:left-4 lg:rounded-[1.75rem] lg:border lg:border-white/70 lg:bg-white/75 lg:shadow-[var(--shadow-elevation-lg)] lg:backdrop-blur-xl",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="mb-6 flex items-center justify-between px-2">
          <ShiftLogo height={22} linked />
          <button type="button" onClick={() => setOpen(false)} className="lg:hidden" aria-label="Close">
            <X className="h-5 w-5 text-muted" />
          </button>
        </div>

        <button type="button" className="workspace-chip mb-6 w-full justify-between">
          <span>Shift Overwatch</span>
          <ChevronDown className="h-4 w-4 text-muted" />
        </button>

        <div className="space-y-6 overflow-y-auto">
          <NavLinks items={mainNav} label="Platform" />
        </div>

        <div className="mt-auto space-y-0.5 border-t border-border/50 pt-4">
          <p className="mb-2 truncate px-3 text-xs text-muted-light" title={displayName}>
            {displayName}
          </p>
          {[
            { icon: Settings, label: "Settings" },
            { icon: HelpCircle, label: "Help" },
          ].map(({ icon: Icon, label }) => (
            <button key={label} type="button" className="nav-item w-full">
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              <span>{label}</span>
            </button>
          ))}
          <button
            type="button"
            className="nav-item w-full"
            onClick={() => void handleSignOut()}
            disabled={signingOut}
          >
            <LogOut className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            <span>{signingOut ? "Signing out…" : "Log out"}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
